/**
 * Работа с внутренней валютой PAWS («лапки»).
 *
 * Все суммы в системе — целые числа в минимальных единицах, никогда не float:
 * 1 PAWS = 1000 минимальных единиц. Дробные лапки существуют только на экране.
 * bigint выбран, чтобы суммы в журнале проводок не теряли точность при
 * агрегации миллионов строк.
 */
export const PAWS_SCALE = 1000n;
export const PAWS_DECIMALS = 3;
/** Множители хранятся целыми в сотых: 2.35x → 235. */
export const MULTIPLIER_SCALE = 100n;
const AMOUNT_PATTERN = /^-?\d+(\.\d+)?$/;
/** Разбирает человекочитаемую сумму («12.5») в минимальные единицы. */
export function parsePaws(input) {
    const raw = typeof input === "number" ? input.toString() : input.trim();
    if (!AMOUNT_PATTERN.test(raw)) {
        throw new Error(`Некорректная сумма PAWS: ${input}`);
    }
    const negative = raw.startsWith("-");
    const [whole = "0", fraction = ""] = raw.replace("-", "").split(".");
    if (fraction.length > PAWS_DECIMALS) {
        throw new Error(`Слишком много знаков после запятой, максимум ${PAWS_DECIMALS}`);
    }
    const padded = fraction.padEnd(PAWS_DECIMALS, "0");
    const value = BigInt(whole) * PAWS_SCALE + BigInt(padded || "0");
    return negative ? -value : value;
}
/** Превращает минимальные единицы в строку для интерфейса. */
export function formatPaws(amount, options = {}) {
    const { decimals, grouping = true } = options;
    const negative = amount < 0n;
    const abs = negative ? -amount : amount;
    const whole = abs / PAWS_SCALE;
    const fraction = abs % PAWS_SCALE;
    let fractionText = fraction.toString().padStart(PAWS_DECIMALS, "0");
    if (decimals === undefined) {
        fractionText = fractionText.replace(/0+$/, "");
    }
    else if (decimals === 0) {
        fractionText = "";
    }
    else {
        fractionText = fractionText.slice(0, decimals).padEnd(decimals, "0");
    }
    let wholeText = whole.toString();
    if (grouping) {
        wholeText = wholeText.replace(/\B(?=(\d{3})+(?!\d))/g, "\u2009");
    }
    const sign = negative ? "-" : "";
    return fractionText ? `${sign}${wholeText},${fractionText}` : `${sign}${wholeText}`;
}
/**
 * Считает выплату по ставке и множителю.
 * Округление вниз — предсказуемо и всегда в сторону заведения, поэтому
 * невозможно набрать дробные единицы «из воздуха» серией мелких ставок.
 */
export function computePayout(stake, multiplier) {
    if (stake < 0n)
        throw new Error("Ставка не может быть отрицательной");
    if (!Number.isInteger(multiplier) || multiplier < 0) {
        throw new Error(`Некорректный множитель: ${multiplier}`);
    }
    return (stake * BigInt(multiplier)) / MULTIPLIER_SCALE;
}
/** Множитель из сотых в число для отображения: 235 → 2.35 */
export function multiplierToNumber(multiplier) {
    return multiplier / Number(MULTIPLIER_SCALE);
}
/** Форматирует множитель как «2.35x». */
export function formatMultiplier(multiplier) {
    return `${multiplierToNumber(multiplier).toFixed(2)}x`;
}
/** Короткая подпись для узких ячеек плинко: «5x», «2.5x», «0.5x». */
export function formatCompactMultiplier(multiplier) {
    const value = multiplierToNumber(multiplier);
    if (value >= 10)
        return `${Math.round(value)}x`;
    const rounded = Math.round(value * 10) / 10;
    return Number.isInteger(rounded) ? `${rounded}x` : `${rounded.toFixed(1)}x`;
}
/** Безопасно разбирает сумму, пришедшую из JSON строкой. */
export function pawsFromJson(value) {
    if (typeof value === "bigint")
        return value;
    if (typeof value === "number") {
        if (!Number.isSafeInteger(value))
            throw new Error("Сумма вне безопасного диапазона");
        return BigInt(value);
    }
    if (!/^-?\d+$/.test(value))
        throw new Error(`Некорректная сумма: ${value}`);
    return BigInt(value);
}
/** Суммы уходят клиенту строками: JSON не умеет bigint. */
export function pawsToJson(amount) {
    return amount.toString();
}
//# sourceMappingURL=money.js.map